import { 
  TableItemSource, 
  NormalizedTableItem,
  SuggestedAssuranceTask,
  AISuggestedTaskItem
} from './types';
import { ControlData } from '@/services/soaUpdatesService';

/**
 * Convert AI suggested tasks to unified table format
 */
export const convertAISuggestionsToTableItems = (
  suggestions: SuggestedAssuranceTask[]
): NormalizedTableItem[] => {
  console.log('[unified-table-utils] Converting AI suggestions to table items:', suggestions);
  const items: NormalizedTableItem[] = [];
  
  suggestions.forEach(suggestion => {
    // Skip suggestion documents that are marked as superseded
    if (suggestion.status === 'superseded') {
      console.log(`[unified-table-utils] Skipping suggestion ${suggestion.id} as it is superseded`);
      return;
    }
    
    const suggestionId = suggestion.id;
    const documentName = suggestion.file_name || 'Unknown Document';
    
    // Process each task in the suggestion
    if (Array.isArray(suggestion.suggested_tasks)) {
      suggestion.suggested_tasks.forEach((task: AISuggestedTaskItem, index: number) => {
        const baseId = task.cast_id || `TASK-${index + 1}`;
        const taskId = `${suggestionId}_${baseId}`; // Combine to ensure uniqueness

        console.log(`[unified-table-utils] Processing task ${taskId} in suggestion ${suggestionId}`);
        
        // Skip tasks that are already accepted or rejected
        const individualTaskStatus = task.status;
        if (individualTaskStatus === 'accepted' || individualTaskStatus === 'rejected') {
          console.log(`[unified-table-utils] Skipping task ${taskId} as it is already ${individualTaskStatus}`);
          return;
        }
        
        items.push({
          id: taskId,
          itemType: TableItemSource.AI_SUGGESTION,
          sourceId: suggestionId,
          originalId: baseId,
          priority: task.priority,
          confidence: task.confidence,
          description: task.task_description,
          evidence: task.evidence_description,
          isoClauses: Array.isArray(task.iso_clauses) ? task.iso_clauses : 
                    (task.iso_clauses ? [task.iso_clauses] : []),
          rationale: task.rationale,
          documentName
        });
      });
    }
  });
  
  return items;
};

/**
 * Convert SOA controls to unified table format
 */
export const convertSOAControlsToTableItems = (
  controls: ControlData[]
): NormalizedTableItem[] => {
  // No need to filter here, filtering is now done at the service level
  
  return controls.map((control, index) => {
    // Extract the control ID and number
    const controlID = control.controlID || `control-${index}`;
    const controlNumber = controlID.replace(/^[^0-9]*/, ''); // Extract the numeric part
    
    return {
      id: `soa_${controlID}`,
      itemType: TableItemSource.SOA_CONTROL,
      sourceId: controlID,
      originalId: controlID,
      description: control.controlData?.description || 'No description available',
      status: control.currentImplementationStatus || control.implementationStatus || control.status || 'Not Implemented',
      implementationDetails: control.controlData?.implementationDetails || '',
      controlNumber
    };
  });
};

/**
 * Combine AI suggestions and SOA controls into a single unified table items array
 */
export const combineToUnifiedTableItems = (
  suggestions: SuggestedAssuranceTask[],
  controls: ControlData[]
): NormalizedTableItem[] => {
  const aiItems = convertAISuggestionsToTableItems(suggestions);
  const soaItems = convertSOAControlsToTableItems(controls);
  
  console.log('AI items:', aiItems);
  console.log('SOA items:', soaItems);
  console.log('Combined items:', [...aiItems, ...soaItems]);
  return [...aiItems, ...soaItems];
};
